// Quake 3 / Wolfenstein: Enemy Territory BSP (IBSP version 47) parser.

import type { Vec3 } from './math';

export const BSP_IDENT = 'IBSP';
export const BSP_VERSION = 47;

export enum Lump {
  Entities = 0,
  Shaders = 1,
  Planes = 2,
  Nodes = 3,
  Leafs = 4,
  LeafSurfaces = 5,
  LeafBrushes = 6,
  Models = 7,
  Brushes = 8,
  BrushSides = 9,
  DrawVerts = 10,
  DrawIndexes = 11,
  Fogs = 12,
  Surfaces = 13,
  Lightmaps = 14,
  LightGrid = 15,
  Visibility = 16,
  Count = 17,
}

export enum SurfaceType {
  Bad = 0,
  Planar = 1,
  Patch = 2,
  TriangleSoup = 3,
  Flare = 4,
  Foliage = 5,
}

export interface BspShader {
  name: string;
  surfaceFlags: number;
  contentFlags: number;
}

export interface BspPlane {
  normal: Vec3;
  dist: number;
}

export interface BspNode {
  planeNum: number;
  children: [number, number];
  mins: Vec3;
  maxs: Vec3;
}

export interface BspLeaf {
  cluster: number;
  area: number;
  mins: Vec3;
  maxs: Vec3;
  firstLeafSurface: number;
  numLeafSurfaces: number;
  firstLeafBrush: number;
  numLeafBrushes: number;
}

export interface BspModel {
  mins: Vec3;
  maxs: Vec3;
  firstSurface: number;
  numSurfaces: number;
  firstBrush: number;
  numBrushes: number;
}

export interface BspBrush {
  firstSide: number;
  numSides: number;
  shaderNum: number;
}

export interface BspBrushSide {
  planeNum: number;
  shaderNum: number;
}

export interface BspSurface {
  shaderNum: number;
  fogNum: number;
  surfaceType: SurfaceType;
  firstVert: number;
  numVerts: number;
  firstIndex: number;
  numIndexes: number;
  lightmapNum: number;
  patchWidth: number;
  patchHeight: number;
}

/** Draw vertices stored as flat typed arrays for cheap mesh building. */
export interface BspDrawVerts {
  count: number;
  xyz: Float32Array; // 3 per vert
  st: Float32Array; // 2 per vert
  lightmap: Float32Array; // 2 per vert
  normal: Float32Array; // 3 per vert
  color: Uint8Array; // 4 per vert
}

export interface BspFog {
  shader: string;
  brushNum: number;
  /** -1 = no visible side (the fog is entirely inside). */
  visibleSide: number;
}

/** Potentially visible set: one bit row of clusterBytes per cluster. */
export interface BspVisibility {
  numClusters: number;
  clusterBytes: number;
  data: Uint8Array;
}

export interface Bsp {
  entityString: string;
  shaders: BspShader[];
  planes: BspPlane[];
  nodes: BspNode[];
  leafs: BspLeaf[];
  leafSurfaces: Int32Array;
  leafBrushes: Int32Array;
  models: BspModel[];
  brushes: BspBrush[];
  brushSides: BspBrushSide[];
  verts: BspDrawVerts;
  indexes: Int32Array;
  surfaces: BspSurface[];
  /** Raw 128x128 RGB lightmaps. */
  lightmaps: Uint8Array[];
  /** Raw light grid lump: 8 bytes per point (ambient rgb, directed rgb, direction lng/lat). */
  lightGrid: Uint8Array;
  fogs: BspFog[];
  /** Null when the map was compiled without vis. */
  visibility: BspVisibility | null;
}

export const LIGHTMAP_SIZE = 128;

function readString(bytes: Uint8Array, offset: number, maxLen: number): string {
  let end = offset;
  const limit = offset + maxLen;
  while (end < limit && bytes[end] !== 0) end++;
  let s = '';
  for (let i = offset; i < end; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

/** Just the entity lump of a BSP (map info without parsing the geometry). */
export function readEntityString(bytes: Uint8Array): string {
  if (bytes.length < 8 + Lump.Count * 8 || readString(bytes, 0, 4) !== BSP_IDENT) throw new Error('Not a Quake 3 BSP');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const offset = view.getInt32(8, true);
  const length = view.getInt32(12, true);
  return new TextDecoder('latin1').decode(bytes.subarray(offset, offset + length)).replace(/\0+$/, '');
}

export function parseBsp(buffer: ArrayBuffer): Bsp {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const ident = readString(bytes, 0, 4);
  if (ident !== BSP_IDENT) throw new Error(`Not a Quake 3 BSP (ident "${ident}")`);
  const version = view.getInt32(4, true);
  if (version !== BSP_VERSION) throw new Error(`Unsupported BSP version ${version} (expected ${BSP_VERSION})`);

  const lumps: { offset: number; length: number }[] = [];
  for (let i = 0; i < Lump.Count; i++) {
    lumps.push({ offset: view.getInt32(8 + i * 8, true), length: view.getInt32(12 + i * 8, true) });
  }

  const lumpRecords = <T>(lump: Lump, size: number, read: (o: number) => T): T[] => {
    const { offset, length } = lumps[lump];
    if (length % size) throw new Error(`Lump ${lump} has funny size`);
    const out: T[] = new Array(length / size);
    for (let i = 0; i < out.length; i++) out[i] = read(offset + i * size);
    return out;
  };
  const f = (o: number) => view.getFloat32(o, true);
  const i32 = (o: number) => view.getInt32(o, true);
  const vecF = (o: number): Vec3 => [f(o), f(o + 4), f(o + 8)];
  const vecI = (o: number): Vec3 => [i32(o), i32(o + 4), i32(o + 8)];
  const intLump = (lump: Lump): Int32Array => {
    const { offset, length } = lumps[lump];
    const out = new Int32Array(length / 4);
    for (let i = 0; i < out.length; i++) out[i] = i32(offset + i * 4);
    return out;
  };

  const ent = lumps[Lump.Entities];
  const entityString = new TextDecoder('latin1').decode(bytes.subarray(ent.offset, ent.offset + ent.length)).replace(/\0+$/, '');

  const shaders = lumpRecords(Lump.Shaders, 72, (o) => ({
    name: readString(bytes, o, 64).toLowerCase(),
    surfaceFlags: i32(o + 64),
    contentFlags: i32(o + 68),
  }));
  const planes = lumpRecords(Lump.Planes, 16, (o) => ({ normal: vecF(o), dist: f(o + 12) }));
  const nodes = lumpRecords(Lump.Nodes, 36, (o) => ({
    planeNum: i32(o),
    children: [i32(o + 4), i32(o + 8)] as [number, number],
    mins: vecI(o + 12),
    maxs: vecI(o + 24),
  }));
  const leafs = lumpRecords(Lump.Leafs, 48, (o) => ({
    cluster: i32(o),
    area: i32(o + 4),
    mins: vecI(o + 8),
    maxs: vecI(o + 20),
    firstLeafSurface: i32(o + 32),
    numLeafSurfaces: i32(o + 36),
    firstLeafBrush: i32(o + 40),
    numLeafBrushes: i32(o + 44),
  }));
  const models = lumpRecords(Lump.Models, 40, (o) => ({
    mins: vecF(o),
    maxs: vecF(o + 12),
    firstSurface: i32(o + 24),
    numSurfaces: i32(o + 28),
    firstBrush: i32(o + 32),
    numBrushes: i32(o + 36),
  }));
  const brushes = lumpRecords(Lump.Brushes, 12, (o) => ({ firstSide: i32(o), numSides: i32(o + 4), shaderNum: i32(o + 8) }));
  const brushSides = lumpRecords(Lump.BrushSides, 8, (o) => ({ planeNum: i32(o), shaderNum: i32(o + 4) }));

  const vl = lumps[Lump.DrawVerts];
  const vcount = vl.length / 44;
  const verts: BspDrawVerts = {
    count: vcount,
    xyz: new Float32Array(vcount * 3),
    st: new Float32Array(vcount * 2),
    lightmap: new Float32Array(vcount * 2),
    normal: new Float32Array(vcount * 3),
    color: new Uint8Array(vcount * 4),
  };
  for (let i = 0; i < vcount; i++) {
    const o = vl.offset + i * 44;
    verts.xyz[i * 3] = f(o);
    verts.xyz[i * 3 + 1] = f(o + 4);
    verts.xyz[i * 3 + 2] = f(o + 8);
    verts.st[i * 2] = f(o + 12);
    verts.st[i * 2 + 1] = f(o + 16);
    verts.lightmap[i * 2] = f(o + 20);
    verts.lightmap[i * 2 + 1] = f(o + 24);
    verts.normal[i * 3] = f(o + 28);
    verts.normal[i * 3 + 1] = f(o + 32);
    verts.normal[i * 3 + 2] = f(o + 36);
    verts.color.set(bytes.subarray(o + 40, o + 44), i * 4);
  }

  const surfaces = lumpRecords(Lump.Surfaces, 104, (o) => ({
    shaderNum: i32(o),
    fogNum: i32(o + 4),
    surfaceType: i32(o + 8) as SurfaceType,
    firstVert: i32(o + 12),
    numVerts: i32(o + 16),
    firstIndex: i32(o + 20),
    numIndexes: i32(o + 24),
    lightmapNum: i32(o + 28),
    patchWidth: i32(o + 96),
    patchHeight: i32(o + 100),
  }));

  const lm = lumps[Lump.Lightmaps];
  const lmSize = LIGHTMAP_SIZE * LIGHTMAP_SIZE * 3;
  const lightmaps: Uint8Array[] = [];
  for (let o = lm.offset; o + lmSize <= lm.offset + lm.length; o += lmSize) {
    lightmaps.push(bytes.slice(o, o + lmSize));
  }

  const fogs = lumpRecords(Lump.Fogs, 72, (o) => ({ shader: readString(bytes, o, 64).toLowerCase(), brushNum: i32(o + 64), visibleSide: i32(o + 68) }));

  const vl16 = lumps[Lump.Visibility];
  let visibility: BspVisibility | null = null;
  if (vl16.length >= 8) {
    const numClusters = i32(vl16.offset);
    const clusterBytes = i32(vl16.offset + 4);
    const data = bytes.slice(vl16.offset + 8, vl16.offset + vl16.length);
    if (numClusters > 0 && clusterBytes > 0 && data.length >= numClusters * clusterBytes) visibility = { numClusters, clusterBytes, data };
  }

  return {
    entityString,
    shaders,
    planes,
    nodes,
    leafs,
    leafSurfaces: intLump(Lump.LeafSurfaces),
    leafBrushes: intLump(Lump.LeafBrushes),
    models,
    brushes,
    brushSides,
    verts,
    indexes: intLump(Lump.DrawIndexes),
    surfaces,
    lightmaps,
    lightGrid: bytes.slice(lumps[Lump.LightGrid].offset, lumps[Lump.LightGrid].offset + lumps[Lump.LightGrid].length),
    fogs,
    visibility,
  };
}

/**
 * R_ClusterPVS + the bit test of R_MarkLeaves: can a viewer in cluster `from`
 * see leaves of cluster `to`? Without vis data (or outside the map) everything is.
 */
export function clusterVisible(vis: BspVisibility | null, from: number, to: number): boolean {
  if (!vis || from < 0 || from >= vis.numClusters) return true;
  if (to < 0 || to >= vis.numClusters) return false;
  return (vis.data[from * vis.clusterBytes + (to >> 3)] & (1 << (to & 7))) !== 0;
}

/** Renderer R_PointInLeaf (d > 0 goes to the front child): leaf index containing p. */
export function pointInLeaf(bsp: Pick<Bsp, 'nodes' | 'planes'>, p: ArrayLike<number>): number {
  if (!bsp.nodes.length) return 0;
  let num = 0;
  while (num >= 0) {
    const node = bsp.nodes[num];
    const pl = bsp.planes[node.planeNum];
    const d = p[0] * pl.normal[0] + p[1] * pl.normal[1] + p[2] * pl.normal[2] - pl.dist;
    num = d > 0 ? node.children[0] : node.children[1];
  }
  return -1 - num;
}

export interface LightGrid {
  origin: Vec3;
  size: Vec3;
  bounds: [number, number, number];
  /** 8 bytes per point, x fastest. */
  data: Uint8Array;
}

/**
 * R_LoadLightGrid: the grid spans the world model's bounds in steps of the
 * worldspawn "gridsize" (64 64 128). Null when the lump does not match (maps
 * compiled without light).
 */
export function lightGridOf(bsp: Pick<Bsp, 'models' | 'lightGrid' | 'entityString'>): LightGrid | null {
  const world = bsp.models[0];
  if (!world || !bsp.lightGrid.length) return null;
  const size: Vec3 = [64, 64, 128];
  // the first entity is worldspawn
  const m = /^\s*\{[^}]*?"gridsize"\s+"([^"]+)"/i.exec(bsp.entityString);
  if (m) {
    const v = m[1].trim().split(/\s+/).map(Number);
    if (v.length === 3 && v.every((x) => x > 0)) size.splice(0, 3, ...v);
  }
  const origin: Vec3 = [0, 0, 0];
  const bounds: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    origin[i] = size[i] * Math.ceil(world.mins[i] / size[i]);
    const max = size[i] * Math.floor(world.maxs[i] / size[i]);
    bounds[i] = Math.trunc((max - origin[i]) / size[i] + 1);
  }
  if (bounds[0] * bounds[1] * bounds[2] * 8 !== bsp.lightGrid.length) return null;
  return { origin, size, bounds, data: bsp.lightGrid };
}

export interface GridLight {
  /** 0..255 scale (before r_ambientScale / r_directedScale). */
  ambient: Vec3;
  directed: Vec3;
  /** Unit vector towards the light. */
  dir: Vec3;
}

/** R_SetupEntityLightingGrid: trilinear blend of the 8 surrounding points, skipping points inside walls. */
export function sampleLightGrid(grid: LightGrid, point: ArrayLike<number>): GridLight {
  const pos = [0, 0, 0];
  const frac = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const v = (point[i] - grid.origin[i]) / grid.size[i];
    pos[i] = Math.floor(v);
    frac[i] = v - pos[i];
    if (pos[i] < 0) pos[i] = 0;
    else if (pos[i] >= grid.bounds[i] - 1) pos[i] = grid.bounds[i] - 1;
  }
  const step = [8, 8 * grid.bounds[0], 8 * grid.bounds[0] * grid.bounds[1]];
  const base = pos[0] * step[0] + pos[1] * step[1] + pos[2] * step[2];
  const ambient: Vec3 = [0, 0, 0];
  const directed: Vec3 = [0, 0, 0];
  const dir: Vec3 = [0, 0, 0];
  let total = 0;
  for (let i = 0; i < 8; i++) {
    let factor = 1;
    let o = base;
    for (let j = 0; j < 3; j++) {
      if (i & (1 << j)) {
        factor *= frac[j];
        o += step[j];
      } else factor *= 1 - frac[j];
    }
    const d = grid.data;
    if (o + 7 >= d.length || !(d[o] + d[o + 1] + d[o + 2])) continue; // ignore samples in walls
    total += factor;
    for (let k = 0; k < 3; k++) {
      ambient[k] += factor * d[o + k];
      directed[k] += factor * d[o + 3 + k];
    }
    const lat = (d[o + 7] * 2 * Math.PI) / 256;
    const lng = (d[o + 6] * 2 * Math.PI) / 256;
    dir[0] += factor * Math.cos(lat) * Math.sin(lng);
    dir[1] += factor * Math.sin(lat) * Math.sin(lng);
    dir[2] += factor * Math.cos(lng);
  }
  if (total > 0 && total < 0.99) {
    for (let k = 0; k < 3; k++) {
      ambient[k] /= total;
      directed[k] /= total;
    }
  }
  const len = Math.hypot(dir[0], dir[1], dir[2]);
  if (len) for (let k = 0; k < 3; k++) dir[k] /= len;
  return { ambient, directed, dir };
}
